import { useState, useCallback, useMemo } from 'react';
import type { DataGridColumn } from '../DataGrid.types';

interface UseColumnVisibilityOptions<T> {
  columns: DataGridColumn<T>[];
  initialHiddenIds?: string[];
}

interface UseColumnVisibilityReturn<T> {
  visibleColumns: DataGridColumn<T>[];
  hiddenColumnIds: string[];
  toggleColumn: (columnId: string) => void;
  showColumn: (columnId: string) => void;
  hideColumn: (columnId: string) => void;
  showAllColumns: () => void;
  hideAllColumns: () => void;
  isColumnVisible: (columnId: string) => boolean;
  setHiddenColumns: (columnIds: string[]) => void;
}

export function useColumnVisibility<T extends Record<string, unknown>>({
  columns,
  initialHiddenIds = []
}: UseColumnVisibilityOptions<T>): UseColumnVisibilityReturn<T> {
  const [hiddenColumnIdsSet, setHiddenColumnIdsSet] = useState<Set<string>>(
    new Set(initialHiddenIds)
  );

  // Convert Set to array for external use
  const hiddenColumnIds = useMemo(() => Array.from(hiddenColumnIdsSet), [hiddenColumnIdsSet]);

  const visibleColumns = useMemo(() => {
    return columns.filter(col => !hiddenColumnIdsSet.has(col.id));
  }, [columns, hiddenColumnIdsSet]);

  const isColumnVisible = useCallback((columnId: string): boolean => {
    return !hiddenColumnIdsSet.has(columnId);
  }, [hiddenColumnIdsSet]);

  const toggleColumn = useCallback((columnId: string) => {
    setHiddenColumnIdsSet(prev => {
      const newSet = new Set(prev);
      if (newSet.has(columnId)) {
        newSet.delete(columnId);
      } else {
        newSet.add(columnId);
      }
      return newSet;
    });
  }, []);

  const showColumn = useCallback((columnId: string) => {
    setHiddenColumnIdsSet(prev => {
      const newSet = new Set(prev);
      newSet.delete(columnId);
      return newSet;
    });
  }, []);

  const hideColumn = useCallback((columnId: string) => {
    setHiddenColumnIdsSet(prev => {
      const newSet = new Set(prev);
      newSet.add(columnId);
      return newSet;
    });
  }, []);

  const showAllColumns = useCallback(() => {
    setHiddenColumnIdsSet(new Set());
  }, []);

  const hideAllColumns = useCallback(() => {
    setHiddenColumnIdsSet(new Set(columns.map(col => col.id)));
  }, [columns]);

  const setHiddenColumns = useCallback((columnIds: string[]) => {
    setHiddenColumnIdsSet(new Set(columnIds));
  }, []);

  return {
    visibleColumns,
    hiddenColumnIds,
    toggleColumn,
    showColumn,
    hideColumn,
    showAllColumns,
    hideAllColumns,
    isColumnVisible,
    setHiddenColumns
  };
}
